import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import CustomerCreditList from 'components/Lists/CustomerCreditList'
import AddCustomerCreditModal from 'pages/Modals/AddCustomerCreditModal'
import AddCustomerDebtModal from 'pages/Modals/AddCustomerDebtModal'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaPlus } from 'react-icons/fa'
import { DateTime } from 'luxon'
import { Container, Row } from '../styles'
import SecondaryButton from 'components/Buttons/SecondaryButton'

export default function CustomerCredits ({
  customer,
  onBalanceUpdate: handleBalanceUpdate
}) {
  const [loading, setLoading] = useState (true)
  const [credits, setCredits] = useState ([])
  const [showAddCreditModal, setShowAddCreditModal] = useState (false)
  const [showAddDebtModal, setShowAddDebtModal] = useState (false)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  
  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getCredits ()
  }, [customer])

  const getCredits = async () => {
    try {
      const { data: credits } = await cutileiApi.get (
        `/businesses/${businessId}/credits?customer_id=${customer.id}`,
        requestConfig
      )

      const { data: debts } = await cutileiApi.get (
        `/businesses/${businessId}/debts?customer_id=${customer.id}`,
        requestConfig
      )

      setCredits ([
        ...credits.map (credit => ({...credit, type: 'credit'})),
        ...debts.map (debt => ({...debt, value: debt.value * -1, type: 'debt'}))
      ].sort (compareCredits))
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const compareCredits = (a, b) => (
    DateTime.fromISO (b.created_at).toMillis () - DateTime.fromISO (a.created_at).toMillis ()
  )

  const handleCreditAdded = credit => {
    setCredits ([...credits, credit].sort (compareCredits))
    const customerBalance = customer.balance + credit.value
    handleBalanceUpdate (customerBalance)
  }

  const handleDebtAdded = debt => {
    setCredits ([...credits, {...debt, value: debt.value * -1}].sort (compareCredits))
    const customerBalance = customer.balance - debt.value
    handleBalanceUpdate (customerBalance)
  }

  const handleCreditEdit = credit => {
    setCredits (credits.map (c => c.id === credit.id ? {...credit} : c).sort (compareCredits))
    const balanceDifference = credit.value - credits.find (c => c.id === credit.id).value
    const customerBalance = customer.balance + balanceDifference
    handleBalanceUpdate (customerBalance)
  }

  const handleCreditDelete = credit => {
    setCredits (credits.filter (c => c.id !== credit.id))
    const customerBalance = customer.balance - credit.value
    handleBalanceUpdate (customerBalance)
  }

  const toggleAddCreditModal = () => setShowAddCreditModal (!showAddCreditModal)
  const toggleAddDebtModal = () => setShowAddDebtModal (!showAddDebtModal)

  return (
    <Container style={{paddingInline: 10}}>
      {AuthService.hasPermission ('customers', 'edit') && (
        <Row style={{marginBottom: 18}}>
          <SecondaryButton
            label='Crédito'
            leftIcon={<FaPlus size={12} color='#FFC100' style={{marginBottom: 1}}/>}
            onClick={toggleAddCreditModal}
            width={120}
          />
          <SecondaryButton
            label='Dívida'
            leftIcon={<FaPlus size={12} color='#FFC100' style={{marginBottom: 1}}/>}
            onClick={toggleAddDebtModal}
            width={120}
          />
        </Row>
      )}
      {loading ? <Loading style={{marginBottom: 20}}/> : (
        <CustomerCreditList
          credits={credits}
          onEdit={handleCreditEdit}
          onDelete={handleCreditDelete}
          onCloseBill={getCredits}
        />
      )}
      <AddCustomerCreditModal
        visible={showAddCreditModal}
        customer={customer}
        onConfirm={handleCreditAdded}
        onClose={toggleAddCreditModal}
      />
      <AddCustomerDebtModal
        visible={showAddDebtModal}
        customer={customer}
        onConfirm={handleDebtAdded}
        onClose={toggleAddDebtModal}
      />
    </Container>
  )
}
